/**
 * /reservations route: the search schema and the router wiring around ReservationsListPage.
 * Permission check: redirects to / if the user lacks reservation.list.
 */
import * as React from 'react';
import { createFileRoute, redirect, useNavigate, useSearch } from '@tanstack/react-router';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import { RESERVATION_SORT_KEYS } from '@ooh/contracts';
import { ReservationsListPage, useReservationPermissions } from '@ooh/screens';
import type { ReservationsSearch } from '@ooh/screens';
import { useReservationFilterOptions } from '../../hooks/use-reservation-filter-options';
import { PageTitleContext } from '../__root';

const ReservationsSearchSchema = z
  .object({
    status: z.array(z.string()).default([]).catch([]),
    holderUserId: z.array(z.string()).default([]).catch([]),
    offerId: z.array(z.string()).default([]).catch([]),
    mediaUnitId: z.array(z.string()).default([]).catch([]),
    source: z.array(z.string()).default([]).catch([]),
    /** Client behind the reservation's offer (CU-86cb7ctfw). */
    clientId: z.array(z.string()).default([]).catch([]),
    /** Salesperson owning the reservation's offer (CU-86cb7ctfw). */
    salespersonId: z.array(z.string()).default([]).catch([]),
    dateFrom: z.string().optional(),
    dateTo: z.string().optional(),
    limit: z.coerce.number().int().min(1).max(100).default(50).catch(50),
    offset: z.coerce.number().int().min(0).default(0).catch(0),
    sort: z.enum(RESERVATION_SORT_KEYS).default('date_from').catch('date_from'),
    order: z.enum(['asc', 'desc']).default('asc').catch('asc'),
    /** "Tylko z kolejką" scope toggle (CU-86cb7ctfw). */
    hasQueue: z.boolean().default(false).catch(false),
    /** Row whose queue should start expanded, e.g. from the media-unit panel's "Otwórz pełną kolejkę" deep link. */
    expand: z.coerce.number().int().positive().optional().catch(undefined),
  })
  .strict();

export const Route = createFileRoute('/reservations/')({
  validateSearch: ReservationsSearchSchema,
  beforeLoad: ({ context }) => {
    if (
      context.auth.permissions !== null &&
      !context.auth.permissions.includes('reservation.list')
    ) {
      throw redirect({ to: '/' });
    }
  },
  component: ReservationsPage,
});

export function ReservationsPage() {
  const { t } = useTranslation('reservations');
  const navigate = useNavigate({ from: Route.fullPath });
  const search: ReservationsSearch = useSearch({ from: '/reservations/' });
  const { setPageTitle } = React.useContext(PageTitleContext);
  const permissions = useReservationPermissions();
  const { offerFilterOptions, clientFilterOptions, salespersonFilterOptions } =
    useReservationFilterOptions(permissions.canForceRelease);

  React.useEffect(() => {
    setPageTitle(t('pageTitle'));
  }, [setPageTitle, t]);

  const filterOptions = React.useMemo(
    () => ({
      offers: offerFilterOptions,
      clients: clientFilterOptions,
      salespeople: salespersonFilterOptions,
    }),
    [offerFilterOptions, clientFilterOptions, salespersonFilterOptions],
  );

  const handleSearchChange = React.useCallback(
    (patch: Partial<ReservationsSearch>, options?: { replace?: boolean }) => {
      void navigate({ replace: options?.replace, search: (prev) => ({ ...prev, ...patch }) });
    },
    [navigate],
  );

  return (
    <ReservationsListPage
      search={search}
      onSearchChange={handleSearchChange}
      filterOptions={filterOptions}
    />
  );
}
